import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CaretDown, CheckCircle, WarningCircle, Check } from "@phosphor-icons/react";
import LoaderLoop from "../../shared/loader/LoaderLoop";
import PhoneInput from "../../ui/PhoneInput";
import { useCreateLeadFromLandingPageMutation } from "../../../store/services/nivApi";
import { LANDING_PAGES } from "../../../data/landingPagesConfig";
import MarkerStroke from "./MarkerStroke";
import useReveal from "./useReveal";
import { trackPixelEvent } from "../../../hooks/useFacebookPixel";
import "./home.css";
import "./HomeSolutions.css";

/*
 * Verticals — compact lead-capture band + SEO internal links.
 *
 * Band: a short "see it in your field" pitch with a two-row form (name, phone,
 * optional therapy field, submit). Writes to niv call-requests through
 * createLeadFromLandingPage, same pipeline as HomeContact; the chosen field
 * is prepended to `message` so sales sees it without a schema change.
 *
 * SEO: every /for/:slug vertical page stays linked below the band with the
 * keyword-bearing anchor text "תוכנה לניהול קליניקה ל{profession}". The
 * anchor-text pattern is load-bearing for link equity — do not change it.
 */

const VERTICALS = Object.values(LANDING_PAGES);
const OTHER_FIELD = "__other";

function HomeSolutions({ source = "homepage_solutions" }) {
  const ref = useReveal();
  const [formData, setFormData] = useState({ fullName: "", phone: "", field: "" });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [createLead, { isLoading }] = useCreateLeadFromLandingPageMutation();

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (errors[key] || errors.submit) setErrors((prev) => ({ ...prev, [key]: "", submit: "" }));
  };

  const validate = () => {
    const next = {};
    if (!formData.fullName.trim()) next.fullName = "יש להזין שם מלא";
    const digits = (formData.phone || "").replace(/\D/g, "");
    const isInternational = formData.phone?.startsWith("+972") && digits.length === 12;
    const isLocal = digits.length === 10 && digits.startsWith("05");
    if (!isInternational && !isLocal) next.phone = "יש להזין מספר נייד ישראלי תקין";
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = validate();
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    const fieldLabel =
      formData.field === OTHER_FIELD
        ? "אחר"
        : VERTICALS.find((v) => v.slug === formData.field)?.profession || "";

    try {
      await createLead({
        fullName: formData.fullName,
        phone: formData.phone,
        message: fieldLabel ? `תחום טיפול: ${fieldLabel}` : "",
        source,
      }).unwrap();
      trackPixelEvent("Lead", { content_name: source });
      setSubmitted(true);
    } catch (err) {
      console.error("[CK-1054] HomeSolutions lead submit failed:", {
        errorCode: "CK-1054",
        errorMessage: err?.message,
        source,
        timestamp: new Date().toISOString(),
      });
      setErrors({ submit: "השליחה נכשלה. אפשר לנסות שוב או להשאיר פרטים בטופס שבתחתית העמוד." });
    }
  };

  return (
    <section id="solutions" className="home-section home-section--gray hsol" aria-labelledby="hsol-title">
      <div className="home-container">
        <div className="hsol-band home-card home-reveal" ref={ref}>
          <div className="hsol-copy">
            <h2 id="hsol-title" className="home-h2 home-h2--display hsol-title">
              רוצים{" "}
              <span className="home-mark">
                לנסות
                <MarkerStroke />
              </span>{" "}
              את זה על הקליניקה שלכם?
            </h2>
            <p className="hsol-sub">
              השאירו שם וטלפון ונחזור אליכם לשיחה קצרה עם הדגמה שמותאמת לקליניקה שלכם.
              בלי התחייבות ובלי כרטיס אשראי.
            </p>
          </div>

          {submitted ? (
            <div className="hsol-success" role="status">
              <CheckCircle size={40} weight="fill" />
              <div>
                <strong>תודה, קיבלנו את הפרטים.</strong>
                <span>נחזור אליכם תוך יום עסקים אחד לתיאום ההדגמה.</span>
              </div>
            </div>
          ) : (
            <form className="hsol-form" onSubmit={handleSubmit} noValidate>
              <div className="hsol-field">
                <label htmlFor="hsol-name" className="hsol-sr">
                  שם מלא
                </label>
                <input
                  id="hsol-name"
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => handleChange("fullName", e.target.value)}
                  placeholder="שם מלא"
                  className={`hsol-input${errors.fullName ? " has-error" : ""}`}
                  disabled={isLoading}
                  autoComplete="name"
                />
                {errors.fullName && <span className="hsol-error">{errors.fullName}</span>}
              </div>

              <div className="hsol-field">
                <span className="hsol-sr">טלפון נייד</span>
                <PhoneInput
                  value={formData.phone}
                  onChange={(val) => handleChange("phone", val)}
                  error={errors.phone}
                  disabled={isLoading}
                />
              </div>

              <div className="hsol-field hsol-field--select">
                <label htmlFor="hsol-field" className="hsol-sr">
                  תחום הטיפול
                </label>
                <select
                  id="hsol-field"
                  value={formData.field}
                  onChange={(e) => handleChange("field", e.target.value)}
                  className={`hsol-input hsol-select${formData.field ? "" : " is-placeholder"}`}
                  disabled={isLoading}
                >
                  <option value="">תחום הטיפול (לא חובה)</option>
                  {VERTICALS.map((v) => (
                    <option key={v.slug} value={v.slug}>
                      {v.profession}
                    </option>
                  ))}
                  <option value={OTHER_FIELD}>אחר</option>
                </select>
                <CaretDown size={16} weight="bold" className="hsol-select-caret" aria-hidden="true" />
              </div>

              <button type="submit" className="home-btn home-btn--primary hsol-submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <LoaderLoop size="xs" />
                    שולח...
                  </>
                ) : (
                  <>
                    חזרו אליי להדגמה
                    <ArrowLeft size={18} weight="bold" />
                  </>
                )}
              </button>

              {errors.submit && (
                <div className="hsol-submit-error" role="alert">
                  <WarningCircle size={16} weight="fill" />
                  <span>{errors.submit}</span>
                </div>
              )}

              <p className="hsol-fineprint">
                <Check size={12} weight="bold" /> נחזור תוך יום עסקים · ללא ספאם · הפרטים לא מועברים לאף גורם
              </p>
            </form>
          )}
        </div>

        <nav className="hsol-seo" aria-label="עמודים ייעודיים לפי תחום טיפול">
          <p className="hsol-seo-label">עמוד ייעודי לכל תחום טיפול:</p>
          <ul className="hsol-seo-list">
            {VERTICALS.map((v) => (
              <li key={v.slug}>
                <Link to={`/for/${v.slug}`} className="hsol-seo-link">
                  תוכנה לניהול קליניקה ל{v.profession}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </section>
  );
}

export default HomeSolutions;
